import { X, Phone, PhoneForwarded, Mail, Linkedin, MessageCircle, Presentation } from 'lucide-react';

export type ScriptType = 'cold_call' | 'followup_call' | 'email' | 'linkedin' | 'whatsapp' | 'meeting_agenda';

interface ScriptTypeSelectorProps {
  onSelect: (scriptType: ScriptType) => void;
  onClose: () => void;
}

const scriptTypes: { key: ScriptType; icon: typeof Phone; label: string; description: string; iconColor: string; iconBg: string }[] = [
  {
    key: 'cold_call',
    icon: Phone,
    label: 'Cold Call Script',
    description: 'First-time outreach calling script',
    iconColor: 'text-[#1875FF]',
    iconBg: 'bg-[#1875FF]/10',
  },
  {
    key: 'followup_call',
    icon: PhoneForwarded,
    label: 'Follow-Up Call Script',
    description: 'Script for follow-up conversations',
    iconColor: 'text-[#05E0D1]',
    iconBg: 'bg-[#05E0D1]/10',
  },
  {
    key: 'email',
    icon: Mail,
    label: 'Email Template',
    description: 'Cold or follow-up email copy',
    iconColor: 'text-[#1875FF]',
    iconBg: 'bg-[#1875FF]/10',
  },
  {
    key: 'linkedin',
    icon: Linkedin,
    label: 'LinkedIn Message',
    description: 'Connection request or DM copy',
    iconColor: 'text-[#05E0D1]',
    iconBg: 'bg-[#05E0D1]/10',
  },
  {
    key: 'whatsapp',
    icon: MessageCircle,
    label: 'WhatsApp Message',
    description: 'First message or follow-up copy',
    iconColor: 'text-[#1875FF]',
    iconBg: 'bg-[#1875FF]/10',
  },
  {
    key: 'meeting_agenda',
    icon: Presentation,
    label: 'Meeting Script / Agenda',
    description: 'Meeting flow, talking points, and agenda',
    iconColor: 'text-[#05E0D1]',
    iconBg: 'bg-[#05E0D1]/10',
  },
];

export function ScriptTypeSelector({ onSelect, onClose }: ScriptTypeSelectorProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden border border-gray-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div>
            <h3 className="text-lg font-extrabold text-gray-900 font-display">What Script Do You Need?</h3>
            <p className="text-sm text-gray-500 mt-0.5">Select the type of script to generate</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
          {scriptTypes.map((st) => {
            const Icon = st.icon;
            return (
              <button
                key={st.key}
                onClick={() => onSelect(st.key)}
                className="w-full flex items-center gap-3.5 p-3.5 rounded-xl border border-gray-100 hover:border-[#1875FF]/40 hover:bg-[#1875FF]/5 transition-all text-left group"
              >
                <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${st.iconBg} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                  <Icon size={20} className={st.iconColor} />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-900 text-sm group-hover:text-[#1875FF] transition-colors">{st.label}</h4>
                  <p className="text-xs text-gray-500 mt-0.5">{st.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
